import React, { createRef, RefObject, useEffect, useRef, useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {
  IShippingAddressForm,
  IShippingDest,
} from "features/mst_shipping_dest/types/client_detail";
import Notify from "config/notify";
import Specs, { SpecRef } from "components/Label/Specs";
import { MenuItem, Select } from "@mui/material";
import prefecture from "util/prefecture";
import { ValidateOption } from "hooks/useValidateInput";
import { MESSAGE } from "constants/validate";
import { DeleteBtnSxProps, ModalSxProps, UpdateBtnSxProps } from "./styles";
import { formatDateTime } from "../../../../utils";

interface IShippingDetailModalProps {
  shippingDest?: IShippingDest;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (isDelete?: boolean) => Promise<any>;
  shippingDestForm: IShippingAddressForm;
  onChange: (data: any) => any;
  isFetching: boolean;
}

const fields: {
  label: string;
  key: keyof IShippingAddressForm;
  disabled: boolean;
  isTextField: boolean;
  validateOption?: ValidateOption;
}[] = [
  {
    label: "得意先コード",
    key: "client_cd",
    disabled: true,
    isTextField: true,
  },
  {
    label: "得意先名",
    key: "client_name",
    disabled: true,
    isTextField: true,
  },
  {
    label: "発送先コード",
    key: "id",
    disabled: true,
    isTextField: true,
  },
  {
    label: "発送先名",
    key: "shipping_dest_name",
    disabled: false,
    isTextField: true,
    validateOption: {
      required: {
        message: MESSAGE.EMPTY,
      },
      maxLength: {
        value: 100,
        message: MESSAGE.MAX_LENGTH(100),
      },
    },
  },
  {
    label: "郵便番号",
    key: "postal_cd",
    disabled: false,
    isTextField: true,
    validateOption: {
      required: {
        message: MESSAGE.EMPTY,
      },
      maxLength: {
        value: 8,
        message: MESSAGE.MAX_LENGTH(8),
      },
    },
  },
  {
    label: "都道府県",
    key: "pref_cd",
    disabled: false,
    isTextField: false,
    validateOption: {
      required: {
        message: MESSAGE.EMPTY,
      },
    },
  },
  {
    label: "発送先住所",
    key: "address",
    disabled: false,
    isTextField: true,
    validateOption: {
      required: {
        message: MESSAGE.EMPTY,
      },
      maxLength: {
        value: 256,
        message: MESSAGE.MAX_LENGTH(256),
      },
    },
  },
  {
    label: "発送先電話番号",
    key: "shipping_dest_tel",
    disabled: false,
    isTextField: true,
    validateOption: {
      required: {
        message: MESSAGE.EMPTY,
      },
      halfWidth: {
        message: MESSAGE.HALFWIDTH,
      },
      includeNumberOnly: {
        message: MESSAGE.INCLUDE_NUMBER,
      },
      maxLength: {
        value: 20,
        message: MESSAGE.MAX_LENGTH(20),
      },
    },
  },
  {
    label: "発送先FAX番号",
    key: "fax_no",
    disabled: true,
    isTextField: true,
  },
];

/**
 * ShippingDetailModal component renders a modal for displaying and updating shipping destination details.
 * @param {IShippingDetailModalProps} props - The properties for the ShippingDetailModal component.
 * @param {IShippingDest} props.shippingDest - The shipping destination details.
 * @param {boolean} props.isOpen - A boolean indicating whether the modal is open.
 * @param {() => void} props.onClose - A function to call when the modal is closed.
 * @returns {Element} The rendered ShippingDetailModal component.
 */
function ShippingDetailModal({
  shippingDest,
  isOpen,
  onClose,
  onSubmit,
  shippingDestForm,
  onChange,
  isFetching,
}: IShippingDetailModalProps) {
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const prevErrors = useRef<{ [key: string]: boolean }>(errors);
  const inputRef = useRef<RefObject<SpecRef>[]>([]);

  fields.forEach((_, index) => {
    if (!inputRef.current[index] && fields[index].isTextField && !fields[index].disabled) {
      inputRef.current[index] = createRef<SpecRef>();
    }
  });
  const [prefCdError, setPrefCdError] = useState("");

  const isHasError = Object.values(errors).some((error) => error);

  const checkingPrefCd = () => {
    if (!shippingDestForm.pref_cd) {
      setErrors((pre) => ({ ...pre, pref_cd: true }));
      setPrefCdError(MESSAGE.EMPTY);
    } else {
      setErrors((pre) => ({ ...pre, pref_cd: false }));
      setPrefCdError("");
    }
  };

  useEffect(() => {
    if (prevErrors.current.pref_cd === undefined) return;
    checkingPrefCd();
  }, [shippingDestForm.pref_cd]);

  useEffect(() => {
    prevErrors.current = errors;
  }, [errors]);

  const checkErrorMannually = () => {
    checkingPrefCd();
    if (shippingDest) return false;
    return inputRef.current.every((ref) => ref.current?.validate());
  };

  const handleSubmit = async () => {
    if (isHasError) return;
    if (checkErrorMannually()) return;
    await onSubmit();
  };

  const confirmDelete = async () => {
    Notify.setConfirm({
      show: true,
      title: "削除しますか？",
      onResult: async (result: boolean) => {
        if (result) {
          await onSubmit(true);
        }
      },
    });
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box sx={ModalSxProps}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography
            fontSize={{ xs: 24, md: 28, lg: 23 }}
            fontWeight="medium"
            color="var(--gray-700)"
          >
            発送先登録 / 更新
          </Typography>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              backgroundColor: "var(--white)",
              fontSize: "25px",
              color: "var(--gray-700)",
              height: "62px",
              borderRadius: "10px",
              fontWeight: "fontWeightMedium",
              border: "3px solid var(--gray-250)",
              ":hover": {
                border: "3px solid var(--info)",
                color: "var(--info)",
              },
            }}
          >
            閉じる
          </Button>
        </Box>
        <Box display="flex" flexDirection="column" gap={0.5}>
          {fields.map((field, index) =>
            field.isTextField ? (
              <Specs
                ref={inputRef.current[index]}
                key={field.key}
                labelProps={{
                  label: field.label,
                  sxBox: {
                    pl: { xs: "16px", lg: "44px" },
                    width: "40%",
                  },
                  sxTypography: { ml: 0 },
                }}
                sxError={{ width: "60%", ml: "auto" }}
                onHasError={(hasError) => setErrors((pre) => ({ ...pre, [field.key]: hasError }))}
                value={shippingDestForm[field.key]}
                textFieldProps={{
                  value: shippingDestForm[field.key],
                  sx: {
                    flex: 1,
                    borderColor: !errors[field.key] ? "var(--silver-150)" : "var(--red-800)",
                  },
                  sxBox: { width: "100%", flex: 1 },
                  onChangeText: (val) => {
                    onChange({
                      ...shippingDestForm,
                      [field.key]: val,
                    });
                  },
                  validateOption: field.validateOption || {},
                  disabled: field.disabled,
                }}
                sxBoxValue={{ width: { xs: "200px", md: "300px", xl: "434px" } }}
                sxBox={{ width: "100%" }}
                isTextField
              />
            ) : (
              <>
                <Box key={field.key} display="flex" justifyContent="space-between">
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "16px", md: "18px", xl: "20px" },
                      color: "var(--black)",
                      lineHeight: "48px",
                      height: "48px",
                      pl: { xs: "16px", lg: "44px" },
                      bgcolor: "var(--primary-500)",
                      borderRadius: 0,
                      width: "40%",
                      boxSizing: "border-box",
                    }}
                  >
                    都道府県
                  </Typography>
                  <Select
                    value={shippingDestForm.pref_cd}
                    onChange={(e) => {
                      onChange({ ...shippingDestForm, pref_cd: e.target.value });
                    }}
                    sx={{
                      width: "60%",
                      height: "48px",
                      backgroundColor: "var(--white)",
                      border: "3px solid var(--silver-250)",
                      borderColor: !errors.pref_cd ? "var(--silver-150)" : "var(--red-800)",
                      "& .MuiOutlinedInput-root": {
                        height: "100%",
                        padding: "0",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "& .MuiInputBase-input": {
                        height: "100%",
                        padding: "0 14px",
                        lineHeight: "48px",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                    variant="outlined"
                    fullWidth
                  >
                    {prefecture.map((item) => (
                      <MenuItem key={item.code} value={item.code}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                {errors.pref_cd && (
                  <Box
                    sx={{
                      mt: "2px",
                      mb: "6px",
                      color: "var(--red-800)",
                      width: "60%",
                      ml: "auto",
                    }}
                  >
                    {prefCdError}
                  </Box>
                )}
              </>
            ),
          )}

          <Box height="50px" width="100%" mt={2} display="flex" justifyContent="space-between">
            <Button
              sx={UpdateBtnSxProps}
              variant="contained"
              disabled={isFetching || isHasError}
              onClick={handleSubmit}
            >
              {!shippingDest ? "登録" : "更新"}
            </Button>
            {!!shippingDest && (
              <Button variant="contained" sx={DeleteBtnSxProps} onClick={confirmDelete}>
                削除
              </Button>
            )}
          </Box>
          <Box width="100%" mt={1} display="flex" justifyContent="left" gap={4}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                backgroundColor: "var(--white)",
                fontSize: "25px",
                color: "var(--gray-700)",
                height: "62px",
                borderRadius: "10px",
                fontWeight: "fontWeightMedium",
                border: "3px solid var(--gray-250)",
                ":hover": { border: "3px solid var(--info)", color: "var(--info)" },
              }}
            >
              閉じる
            </Button>
            {!!shippingDest && (
              <Typography
                fontSize="21px"
                color="var(--gray-700)"
                sx={{ lineHeight: "100%", my: "auto" }}
              >
                更新日 {formatDateTime(shippingDest.updated_at)}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

export default ShippingDetailModal;
